import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Upload, Loader2, RotateCcw, Type, Save } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";

export default function AdminBranding() {
  const [bs, setBs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const [logoTick, setLogoTick] = useState(0); // cache-buster for preview
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/brand");
      setBs(data);
    } catch (e) { toast.error("Failed to load branding"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/brand/footer", {
        brand_name: bs.brand_name,
        footer_primary: bs.footer_primary,
        footer_secondary: bs.footer_secondary,
        logo_height_mm: bs.logo_height_mm,
        gst_number: bs.gst_number || "",
      });
      toast.success("Branding saved · applies to next PDF render");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) { toast.error("PNG or JPG only"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("optivize_token");
      const r = await fetch(`${API}/admin/brand/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      toast.success("Logo uploaded");
      setLogoTick(t => t + 1);
      load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUpBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const reset = async () => {
    if (!window.confirm("Revert to the default Optivize logo?")) return;
    try {
      await api.delete("/admin/brand/logo");
      toast.success("Reverted to default logo");
      setLogoTick(t => t + 1);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!bs) {
    return <div className="card p-6 text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading branding…</div>;
  }

  const logoUrl = `${API}/brand/logo?v=${logoTick}`;

  return (
    <div className="card p-6 space-y-5" data-testid="admin-branding-card">
      <div>
        <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider"><ImageIcon size={14}/> Branding</div>
        <h3 className="text-xl font-bold text-white mt-1" style={{fontFamily:'Outfit'}}>Logo & PDF footer</h3>
        <p className="text-slate-400 text-sm mt-1">
          Replace the default Optivize branding on every generated PDF (invoices, SEO reports, fulfilment notes).
          Useful for white-label / reseller work.
        </p>
      </div>

      {/* Logo upload */}
      <div className="grid sm:grid-cols-[200px_1fr] gap-4 items-start">
        <div className="bg-slate-900/60 rounded-xl border border-slate-700 aspect-square flex items-center justify-center p-4" data-testid="brand-logo-preview">
          <img src={logoUrl} alt="Current brand logo"
               className="max-w-full max-h-full object-contain"
               key={logoTick}
               onError={(e) => { e.target.style.opacity = 0.2; }}/>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-white font-semibold text-sm">Brand logo</div>
            <div className="text-slate-400 text-xs">PNG or JPG, max 2 MB. Aspect ratio preserved automatically — square or wide both work.</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => fileRef.current?.click()} disabled={upBusy}
                    className="btn-primary !py-2 !px-3 text-sm flex items-center gap-1.5" data-testid="brand-logo-upload-btn">
              {upBusy ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>}
              Upload logo
            </button>
            {bs.has_custom_logo && (
              <button onClick={reset} className="btn-ghost !py-2 !px-3 text-sm flex items-center gap-1.5" data-testid="brand-logo-reset-btn">
                <RotateCcw size={14}/> Revert to default
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg"
                   className="hidden" onChange={e => upload(e.target.files?.[0])}
                   data-testid="brand-logo-file-input"/>
          </div>
          {bs.has_custom_logo && (
            <div className="text-emerald-300 text-xs flex items-center gap-1">
              ✓ Custom logo active
            </div>
          )}
          {/* Logo size slider */}
          <div className="pt-3">
            <div className="flex justify-between items-center mb-1">
              <label className="label !mb-0">PDF logo height</label>
              <span className="text-xs text-slate-400 font-mono" data-testid="brand-logo-size-value">{Number(bs.logo_height_mm || 18).toFixed(0)} mm</span>
            </div>
            <input
              type="range"
              min={10}
              max={36}
              step={1}
              value={Number(bs.logo_height_mm || 18)}
              onChange={(e) => setBs({...bs, logo_height_mm: Number(e.target.value)})}
              className="w-full accent-blue-500"
              data-testid="brand-logo-size-slider"
            />
            <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
              <span>Small (10 mm)</span><span>Default (18 mm)</span><span>XL (36 mm)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer text */}
      <div className="space-y-3 pt-4 border-t border-slate-800">
        <div className="flex items-center gap-2 text-slate-300 text-sm">
          <Type size={14}/> Footer text
        </div>

        <div>
          <label className="label">Brand name (used as fallback if logo fails to load)</label>
          <input value={bs.brand_name || ""} onChange={e => setBs({...bs, brand_name: e.target.value})}
                 className="input w-full" data-testid="brand-name-input"
                 maxLength={80}/>
        </div>

        <div>
          <label className="label">Footer line 1 — copyright</label>
          <input value={bs.footer_primary || ""} onChange={e => setBs({...bs, footer_primary: e.target.value})}
                 className="input w-full" data-testid="brand-footer-primary"
                 maxLength={240}
                 placeholder="© 2026 Your Company · All rights reserved."/>
        </div>

        <div>
          <label className="label">Footer line 2 — contact</label>
          <input value={bs.footer_secondary || ""} onChange={e => setBs({...bs, footer_secondary: e.target.value})}
                 className="input w-full" data-testid="brand-footer-secondary"
                 maxLength={240}
                 placeholder="hello@yourcompany.com · +91 ... · https://yourcompany.com"/>
        </div>

        <div>
          <label className="label">GST / Tax registration number (optional — used in Year-end audit packs)</label>
          <input value={bs.gst_number || ""} onChange={e => setBs({...bs, gst_number: e.target.value.toUpperCase()})}
                 className="input w-full font-mono" data-testid="brand-gst-input"
                 maxLength={20}
                 placeholder="e.g. 19AABCS1234M1ZQ"/>
        </div>

        {/* Live preview */}
        <div className="bg-slate-50 rounded-lg p-4 text-center" data-testid="brand-footer-preview">
          <div className="text-[#475569] text-sm">{bs.footer_primary || "—"}</div>
          <div className="text-[#94a3b8] text-xs mt-0.5">{bs.footer_secondary || "—"}</div>
        </div>

        <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="brand-footer-save-btn">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save footer
        </button>
      </div>

      {/* Multi-brand profiles */}
      <BrandProfiles/>
    </div>
  );
}

// ============================================================================
// MULTI-BRAND PROFILES
// ============================================================================
import { Plus, Trash2, Edit3, X, Building2 } from "lucide-react";
import ModalPortal from "../../components/ModalPortal";

function BrandProfiles() {
  const [profiles, setProfiles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/admin/brand/profiles").then(r => setProfiles(r.data)).catch(() => setProfiles([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this brand profile and its logo?")) return;
    try { await api.delete(`/admin/brand/profiles/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="pt-4 border-t border-slate-800 space-y-3" data-testid="brand-profiles-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-300 text-sm font-semibold">
            <Building2 size={14}/> Multi-brand profiles
          </div>
          <p className="text-slate-500 text-xs mt-0.5">Save reusable brand identities for white-label / agency work. Pick one per PDF render.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid="brand-profile-new-btn">
          <Plus size={13}/> New profile
        </button>
      </div>

      {profiles === null ? (
        <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading…</div>
      ) : profiles.length === 0 ? (
        <div className="text-slate-500 text-sm italic p-3 rounded-lg bg-slate-900/40">No brand profiles yet — create one for each reseller / agency client.</div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="brand-profiles-list">
          {profiles.map(p => (
            <li key={p.id} className="card !bg-slate-900/40 p-3 flex items-center gap-3">
              <div className="w-14 h-14 rounded-lg bg-slate-950/80 border border-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {p.has_logo ? (
                  <img src={`${API}/brand/profiles/${p.id}/logo?v=${p.updated_at || ""}`} alt={p.name}
                       className="max-w-full max-h-full object-contain"/>
                ) : (
                  <Building2 className="text-slate-500" size={20}/>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold truncate">{p.name}</div>
                <div className="text-xs text-slate-400 truncate">{p.brand_name}</div>
              </div>
              <button onClick={() => setEditing(p)} className="text-slate-400 hover:text-white p-1.5" data-testid={`brand-profile-edit-${p.id}`}>
                <Edit3 size={14}/>
              </button>
              <button onClick={() => remove(p.id)} className="text-rose-400 hover:text-rose-300 p-1.5" data-testid={`brand-profile-delete-${p.id}`}>
                <Trash2 size={14}/>
              </button>
            </li>
          ))}
        </ul>
      )}

      {(showNew || editing) && (
        <ProfileEditor
          initial={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProfileEditor({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    brand_name: initial?.brand_name || "",
    footer_primary: initial?.footer_primary || "© 2026 ",
    footer_secondary: initial?.footer_secondary || "",
  });
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [hasLogo, setHasLogo] = useState(initial?.has_logo || false);
  const [logoTick, setLogoTick] = useState(0);
  const fileRef = useRef(null);

  const save = async () => {
    if (form.name.trim().length < 2) { toast.error("Profile name too short"); return; }
    setBusy(true);
    try {
      let id = initial?.id;
      if (isEdit) {
        await api.put(`/admin/brand/profiles/${id}`, form);
      } else {
        const { data } = await api.post("/admin/brand/profiles", form);
        id = data.id;
        initial = data; // so logo upload can target it
      }
      toast.success(isEdit ? "Profile updated" : "Profile created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file || !initial?.id) { toast.error("Save the profile first, then upload its logo"); return; }
    if (!/^image\/(png|jpe?g)$/.test(file.type)) { toast.error("PNG or JPG only"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("optivize_token");
      const r = await fetch(`${API}/admin/brand/profiles/${initial.id}/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      toast.success("Logo uploaded");
      setHasLogo(true);
      setLogoTick(t => t + 1);
    } catch (e) { toast.error(e.message); }
    finally { setLogoBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <ModalPortal><div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
         onClick={onClose} data-testid="brand-profile-editor">
      <div className="card max-w-xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">{isEdit ? "Edit profile" : "New brand profile"}</h3>
          <button onClick={onClose}><X className="text-slate-400" size={18}/></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Profile name (internal label)</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                   placeholder="e.g. 'Acme Reseller'" className="input w-full" data-testid="profile-name-input"/>
          </div>
          <div>
            <label className="label">Brand name (shown on PDFs if logo fails)</label>
            <input value={form.brand_name} onChange={e => setForm({...form, brand_name: e.target.value})}
                   className="input w-full" data-testid="profile-brand-input"/>
          </div>
          <div>
            <label className="label">Footer line 1</label>
            <input value={form.footer_primary} onChange={e => setForm({...form, footer_primary: e.target.value})}
                   className="input w-full" data-testid="profile-footer1-input"/>
          </div>
          <div>
            <label className="label">Footer line 2</label>
            <input value={form.footer_secondary} onChange={e => setForm({...form, footer_secondary: e.target.value})}
                   className="input w-full" data-testid="profile-footer2-input"/>
          </div>

          {initial?.id && (
            <div className="pt-2 border-t border-slate-800">
              <label className="label">Logo</label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg bg-slate-950/80 border border-slate-700 flex items-center justify-center overflow-hidden">
                  {hasLogo ? (
                    <img src={`${API}/brand/profiles/${initial.id}/logo?v=${logoTick}`}
                         className="max-w-full max-h-full object-contain" alt=""/>
                  ) : <Building2 className="text-slate-500" size={22}/>}
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                       onChange={e => uploadLogo(e.target.files?.[0])} data-testid="profile-logo-file"/>
                <button onClick={() => fileRef.current?.click()} disabled={logoBusy}
                        className="btn-primary !py-2 !px-3 text-sm flex items-center gap-1.5" data-testid="profile-logo-upload">
                  {logoBusy ? <Loader2 className="animate-spin" size={13}/> : <Upload size={13}/>}
                  {hasLogo ? "Replace logo" : "Upload logo"}
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-3">
            <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="profile-save-btn">
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
              {isEdit ? "Save" : "Create profile"}
            </button>
            {!isEdit && <p className="text-slate-500 text-xs self-center">Save first, then upload a logo.</p>}
          </div>
        </div>
      </div>
    </div></ModalPortal>
  );
}
